import bidzLogo from "../assets/images/png/Logo@2x.png";

export async function exportStructuredRecordPdf({
  headerTitle,
  headerSubtitle,
  exportDate,
  summaryTitle = "Summary",
  summaryRows = [],
  sections = [],
  documentLinks = [],
  fileName,
}) {
  const { default: jsPDF } = await import("jspdf");
  const autoTableModule = await import("jspdf-autotable");
  const autoTable = autoTableModule.default || autoTableModule.autoTable;

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const margins = { top: 31, right: 12, bottom: 14, left: 12 };
  const contentWidth = pdfWidth - margins.left - margins.right;
  const labelColumnWidth = 58;
  const headingColor = [15, 23, 42];
  const sectionFillColor = [239, 246, 255];
  const borderColor = [226, 232, 240];
  const linkColor = [37, 99, 235];
  const headerTextX = margins.left + 31;
  const drawnHeaderPages = new Set();

  const normalizeRows = (rows = []) =>
    rows
      .filter((row) => Array.isArray(row) && row.length >= 2)
      .map(([label, value]) => [
        valueLabel(label),
        valueLabel(value),
      ]);

  const normalizedSummaryRows = normalizeRows(summaryRows);
  const normalizedSections = sections
    .map((section) => ({
      ...section,
      rows: normalizeRows(section?.rows),
    }))
    .filter((section) => section?.title && section.rows.length);

  const normalizedDocumentLinks = documentLinks.filter(
    (document) => document?.label && document?.url,
  );

  function valueLabel(value) {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
  }

  const renderPageHeader = () => {
    const pageNumber = pdf.internal.getCurrentPageInfo().pageNumber;
    if (drawnHeaderPages.has(pageNumber)) return;
    drawnHeaderPages.add(pageNumber);

    pdf.setFillColor(248, 250, 252);
    pdf.rect(0, 0, pdfWidth, 18, "F");
    pdf.addImage(bidzLogo, "PNG", margins.left, 4, 24, 8);

    pdf.setTextColor(15, 23, 42);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(pageNumber === 1 ? 15 : 13);
    pdf.text(valueLabel(headerTitle), headerTextX, 8);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.text(valueLabel(headerSubtitle), headerTextX, 13);
    pdf.text(`Exported On: ${valueLabel(exportDate)}`, pdfWidth - margins.right, 8, { align: "right" });

    pdf.setDrawColor(...borderColor);
    pdf.setLineWidth(0.2);
    pdf.line(margins.left, 22, pdfWidth - margins.right, 22);
  };

  const addPageWithHeader = () => {
    pdf.addPage();
    renderPageHeader();
  };

  const getLastAutoTableFinalY = () => pdf.lastAutoTable?.finalY ?? margins.top;

  const ensurePageSpace = (requiredHeight = 14) => {
    const currentY = getLastAutoTableFinalY();
    if (currentY + requiredHeight > pdfHeight - margins.bottom) {
      addPageWithHeader();
      return margins.top;
    }
    return currentY;
  };

  const renderSectionTable = (title, rows = [], startY, linkMode = false) => {
    autoTable(pdf, {
      startY,
      margin: { left: margins.left, right: margins.right },
      tableWidth: contentWidth,
      theme: "grid",
      head: [[{
        content: valueLabel(title),
        colSpan: 2,
        styles: {
          fillColor: sectionFillColor,
          textColor: headingColor,
          fontStyle: "bold",
          fontSize: 11,
          halign: "left",
          cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
          lineColor: borderColor,
          lineWidth: 0.2,
        },
      }]],
      body: rows,
      styles: {
        font: "helvetica",
        fontSize: 9,
        textColor: headingColor,
        overflow: "linebreak",
        valign: "top",
        cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
        lineColor: borderColor,
        lineWidth: 0.15,
      },
      bodyStyles: {
        minCellHeight: 6,
      },
      columnStyles: {
        0: {
          cellWidth: labelColumnWidth,
          fontStyle: "bold",
        },
        1: {
          cellWidth: contentWidth - labelColumnWidth,
          textColor: linkMode ? linkColor : headingColor,
        },
      },
      rowPageBreak: "avoid",
      didDrawPage: renderPageHeader,
      didDrawCell: linkMode
        ? (hookData) => {
            if (hookData.section !== "body" || hookData.column.index !== 1) return;
            const document = normalizedDocumentLinks[hookData.row.index];
            if (!document?.url) return;
            pdf.link(hookData.cell.x, hookData.cell.y, hookData.cell.width, hookData.cell.height, {
              url: document.url,
            });
          }
        : undefined,
    });
  };

  renderPageHeader();

  if (normalizedSummaryRows.length) {
    renderSectionTable(summaryTitle, normalizedSummaryRows, margins.top);
  }

  normalizedSections.forEach((section) => {
    const nextY = ensurePageSpace(18) + 5;
    renderSectionTable(section.title, section.rows, nextY);
  });

  if (normalizedDocumentLinks.length) {
    renderSectionTable(
      "Document Links",
      normalizedDocumentLinks.map((document) => [document.label, document.url]),
      ensurePageSpace(18) + 5,
      true,
    );
  }

  pdf.save(fileName);
}
